/**
 * Tableau de proportionnalité — logique pure (Boîte à outils, 6e → 3e).
 *
 * Tout se calcule en fractions exactes (`ui/exact`) : un tableau est
 * proportionnel quand tous les quotients « ligne du bas ÷ ligne du haut » sont
 * égaux (le coefficient). Quatrième proportionnelle par quatre procédures
 * (coefficient, retour à l'unité, linéarité, produit en croix) et partage
 * proportionnel (ratio). Les phrases sont rendues en étapes { tex, say }.
 */

import { add, div, exact, frac, fromDecimal, mul, sub, type Frac } from '../../ui/exact';
import { formatNumber } from '../../ui/format';

export interface Step {
  tex: string;
  say: string;
}

export interface Column {
  x: number;
  y: number;
}

export interface Situation {
  id: string;
  title: string;
  top: string;
  bottom: string;
  cols: Column[];
  /** pourquoi ce n'est pas proportionnel, dit avec les mots de la situation */
  why?: string;
}

const col = (x: number, y: number): Column => ({ x, y });

export const SITUATIONS: readonly Situation[] = [
  {
    id: 'pommes',
    title: 'Prix des pommes',
    top: 'Masse (kg)',
    bottom: 'Prix (€)',
    cols: [col(1, 2.4), col(2, 4.8), col(3, 7.2), col(5, 12)],
  },
  {
    id: 'taxi',
    title: 'Course de taxi',
    top: 'Distance (km)',
    bottom: 'Prix (€)',
    cols: [col(2, 7), col(4, 10), col(8, 16), col(10, 19)],
    why: 'Les 4 € de prise en charge s’ajoutent toujours : doubler la distance ne double pas le prix.',
  },
  {
    id: 'crepes',
    title: 'Recette de crêpes',
    top: 'Personnes',
    bottom: 'Farine (g)',
    cols: [col(4, 250), col(6, 375), col(10, 625), col(2, 125)],
  },
  {
    id: 'perimetre',
    title: 'Périmètre d’un carré',
    top: 'Côté (cm)',
    bottom: 'Périmètre (cm)',
    cols: [col(1, 4), col(2, 8), col(3, 12), col(5, 20)],
  },
  {
    id: 'aire',
    title: 'Aire d’un carré',
    top: 'Côté (cm)',
    bottom: 'Aire (cm²)',
    cols: [col(1, 1), col(2, 4), col(3, 9), col(5, 25)],
    why: 'Quand le côté double, l’aire est multipliée par 4 : elle n’est pas proportionnelle au côté.',
  },
  {
    id: 'taille',
    title: 'Âge et taille',
    top: 'Âge (ans)',
    bottom: 'Taille (cm)',
    cols: [col(2, 86), col(5, 110), col(10, 138), col(15, 170)],
    why: 'À 10 ans, on ne mesure pas deux fois sa taille de 5 ans.',
  },
];

/** « 2,4 », « 1/3 », « −0,5 » */
export const show = (f: Frac) => exact(f).plain;
export const texOf = (f: Frac) => exact(f).tex;
export const fx = (v: number) => fromDecimal(v);
const NNBSP = String.fromCharCode(0x202f);
/** « 12 500 », « 2,4 », « −3 » : milliers groupés */
export function n(v: number): string {
  const t = formatNumber(v, 3);
  const neg = t.startsWith('−');
  const [int, dec] = (neg ? t.slice(1) : t).split(',');
  const grouped = int!.length > 4 ? int!.replace(/\B(?=(\d{3})+(?!\d))/g, NNBSP) : int!;
  return `${neg ? '−' : ''}${grouped}${dec ? `,${dec}` : ''}`;
}
/** nombre négatif entre parenthèses dans un calcul : « 7 ÷ (−2) » */
const np = (v: number) => (v < 0 ? `(${n(v)})` : n(v));
const nTex = (v: number) => n(v).split(NNBSP).join('\\,').replace(',', '{,}').replace('−', '-');

export interface Analysis {
  /** quotient y ÷ x de chaque colonne (null si x = 0) */
  ratios: Array<Frac | null>;
  proportional: boolean;
  coef: Frac | null;
  /** une colonne où ça casse (x = 0 et y ≠ 0, ou quotient différent du premier) */
  breakAt: number | null;
  /** points alignés (droite qui ne passe pas forcément par l'origine) */
  aligned: boolean;
  say: string;
}

/**
 * Linéarité (la procédure de 6e) : une colonne multiple d'une autre, ou
 * somme de deux autres. Un contre-exemple d'abord s'il y en a un.
 */
export function linearityCheck(cols: readonly Column[]): { ok: boolean; say: string } | null {
  const eq = (a: Frac, b: Frac) => a.p === b.p && a.q === b.q;
  const found: Array<{ ok: boolean; say: string }> = [];
  cols.forEach((ci, i) =>
    cols.forEach((cj, j) => {
      if (i === j || ci.x === 0) return;
      const k = div(fx(cj.x), fx(ci.x));
      if (k.q !== 1 || k.p < 2) return;
      const want = mul(k, fx(ci.y));
      const ok = eq(want, fx(cj.y));
      found.push({
        ok,
        say: ok
          ? `${n(cj.x)} = ${k.p} × ${np(ci.x)} et ${n(cj.y)} = ${k.p} × ${np(ci.y)} : quand on multiplie la 1re ligne par ${k.p}, la 2e est aussi multipliée par ${k.p}.`
          : `${n(cj.x)} = ${k.p} × ${np(ci.x)}, mais ${k.p} × ${np(ci.y)} = ${show(want)} et pas ${n(cj.y)} : ce n’est pas proportionnel.`,
      });
    }),
  );
  for (let i = 0; i < cols.length; i++)
    for (let j = i + 1; j < cols.length; j++)
      for (let k = 0; k < cols.length; k++) {
        if (k === i || k === j) continue;
        const [a, b, c] = [cols[i]!, cols[j]!, cols[k]!];
        if (!eq(add(fx(a.x), fx(b.x)), fx(c.x))) continue;
        const want = add(fx(a.y), fx(b.y));
        const ok = eq(want, fx(c.y));
        found.push({
          ok,
          say: ok
            ? `${n(c.x)} = ${n(a.x)} + ${np(b.x)} et ${n(c.y)} = ${n(a.y)} + ${np(b.y)} : on peut additionner les colonnes.`
            : `${n(c.x)} = ${n(a.x)} + ${np(b.x)}, mais ${n(a.y)} + ${np(b.y)} = ${show(want)} et pas ${n(c.y)} : ce n’est pas proportionnel.`,
        });
      }
  return found.find((f) => !f.ok) ?? found[0] ?? null;
}

/** `rank` : en 6e (1), ni coefficient ni produit en croix ; on parle de la valeur « pour 1 » */
export function analyse(cols: readonly Column[], rank = 2): Analysis {
  const ratios = cols.map((c) => (c.x === 0 ? null : div(fx(c.y), fx(c.x))));
  let coef: Frac | null = null;
  let breakAt: number | null = null;
  for (let i = 0; i < cols.length && breakAt === null; i++) {
    const r = ratios[i] ?? null;
    if (r === null) {
      if (cols[i]!.y !== 0) breakAt = i;
    } else if (coef === null) coef = r;
    else if (r.p !== coef.p || r.q !== coef.q) breakAt = i;
  }
  const proportional = breakAt === null && coef !== null;
  const aligned = isAligned(cols);
  let say: string;
  if (proportional) {
    say =
      rank <= 1
        ? `Pour 1, on trouve toujours ${show(coef!)} : c’est une situation de proportionnalité.`
        : `Tous les quotients valent ${show(coef!)} : c’est un tableau de proportionnalité, de coefficient ${show(coef!)}. On passe de la 1re ligne à la 2e en multipliant par ${show(coef!)}.`;
  } else if (breakAt === null) {
    say = 'Il faut au moins une valeur non nulle dans la 1re ligne.';
  } else {
    const i = breakAt!;
    const c = cols[i]!;
    const first = ratios.findIndex((r) => r !== null);
    const f = cols[first]!;
    say =
      c.x === 0
        ? `Pour 0 en haut, il faudrait 0 en bas : ici ${n(c.y)}. Ce n’est pas proportionnel.`
        : rank <= 1
          ? `Pour 1 : ${show(ratios[first]!)} d’après la colonne ${n(f.x)}, mais ${show(ratios[i]!)} d’après la colonne ${n(c.x)}. Ce n’est pas proportionnel.`
          : `${n(f.y)} ÷ ${np(f.x)} = ${show(ratios[first]!)} mais ${n(c.y)} ÷ ${np(c.x)} = ${show(ratios[i]!)} : les quotients ne sont pas égaux, ce n’est pas proportionnel.`;
  }
  return { ratios, proportional, coef, breakAt, aligned, say };
}

/** les points (x, y) sont-ils sur une même droite ? (au moins 3 points distincts) */
function isAligned(cols: readonly Column[]): boolean {
  const pts = cols.map((c) => [fx(c.x), fx(c.y)] as const);
  if (pts.length < 3) return true;
  const [x0, y0] = pts[0]!;
  let dir: readonly [Frac, Frac] | null = null;
  for (const [x, y] of pts.slice(1)) {
    const dx = sub(x, x0);
    const dy = sub(y, y0);
    if (dx.p === 0 && dy.p === 0) continue;
    if (!dir) {
      dir = [dx, dy];
      continue;
    }
    // dx·dir.y − dy·dir.x = 0
    const cross = sub(mul(dx, dir[1]), mul(dy, dir[0]));
    if (cross.p !== 0) return false;
  }
  return true;
}

/** lecture du graphique */
export function graphSay(a: Analysis, count = 3): string {
  if (a.proportional)
    return 'Les points sont alignés sur une droite qui passe par l’origine : c’est la marque de la proportionnalité.';
  if (count < 3)
    return 'La droite qui passe par ces points ne passe pas par l’origine : ce n’est pas proportionnel.';
  if (a.aligned)
    return 'Les points sont alignés, mais la droite ne passe pas par l’origine : ce n’est pas proportionnel.';
  return 'Les points ne sont pas alignés : ce n’est pas proportionnel.';
}

/* ------------------------------------------------------------------ */
/* quatrième proportionnelle                                           */
/* ------------------------------------------------------------------ */

export type Method = 'coef' | 'unit' | 'linear' | 'cross';

export const METHOD_LABELS: Record<Method, string> = {
  coef: 'Coefficient',
  unit: 'Retour à l’unité',
  linear: 'Linéarité',
  cross: 'Rapports égaux',
};

/** x1 → y1, x2 → ? */
export interface Missing {
  x1: number;
  y1: number;
  x2: number;
}

export function missingValue(m: Missing): Frac {
  return div(mul(fx(m.y1), fx(m.x2)), fx(m.x1));
}

/** le rapport x2 / x1 est-il « simple » (entier, ou inverse d'entier) ? */
export function linearFactor(m: Missing): { k: Frac; up: boolean } | null {
  const k = div(fx(m.x2), fx(m.x1));
  if (k.q === 1 && k.p > 1) return { k, up: true };
  if (k.p === 1 && k.q > 1) return { k, up: false };
  return null;
}

export function missingSteps(m: Missing, method: Method): Step[] {
  const X1 = fx(m.x1);
  const Y1 = fx(m.y1);
  const X2 = fx(m.x2);
  const y2 = missingValue(m);
  const res = texOf(y2);
  switch (method) {
    case 'coef': {
      const k = div(Y1, X1);
      return [
        {
          tex: `${nTex(m.y1)} \\div ${nTex(m.x1)} = ${texOf(k)}`,
          say: `Le coefficient : on divise ${n(m.y1)} par ${n(m.x1)}, on trouve ${show(k)}.`,
        },
        {
          tex: `${nTex(m.x2)} \\times ${texOf(k)} = ${res}`,
          say: `On multiplie ${n(m.x2)} par le coefficient.`,
        },
      ];
    }
    case 'unit': {
      const u = div(Y1, X1);
      return [
        {
          tex: `${nTex(m.y1)} \\div ${nTex(m.x1)} = ${texOf(u)}`,
          say: `Pour 1 : on divise par ${n(m.x1)}.`,
        },
        {
          tex: `${texOf(u)} \\times ${nTex(m.x2)} = ${res}`,
          say: `Pour ${n(m.x2)} : on multiplie la valeur pour 1 par ${n(m.x2)}.`,
        },
      ];
    }
    case 'linear': {
      const lf = linearFactor(m);
      if (m.x1 === m.x2)
        return [
          {
            tex: `${nTex(m.x2)} = ${nTex(m.x1)} \\Rightarrow ? = ${nTex(m.y1)}`,
            say: 'Même valeur en haut, donc même valeur en bas.',
          },
        ];
      if (!lf) {
        // décomposer : x2 = x1 + (x2 − x1) quand c'est plus simple ? on reste sur × k exact
        const k = div(X2, X1);
        return [
          {
            tex: `${nTex(m.x2)} = ${nTex(m.x1)} \\times ${texOf(k)}`,
            say: `${n(m.x2)} n’est pas un multiple simple de ${n(m.x1)} : le facteur vaut ${show(k)}. Le retour à l’unité est plus commode ici.`,
          },
          {
            tex: `${nTex(m.y1)} \\times ${texOf(k)} = ${res}`,
            say: 'On multiplie la 2e ligne par le même nombre.',
          },
        ];
      }
      const kTex = lf.up ? `\\times ${lf.k.p}` : `\\div ${lf.k.q}`;
      const kSay = lf.up ? `multiplié par ${lf.k.p}` : `divisé par ${lf.k.q}`;
      return [
        {
          tex: `${nTex(m.x1)} ${kTex} = ${nTex(m.x2)}`,
          say: `En haut, ${n(m.x1)} est ${kSay}.`,
        },
        {
          tex: `${nTex(m.y1)} ${kTex} = ${res}`,
          say: `En bas aussi : même opération sur la 2e ligne.`,
        },
      ];
    }
    case 'cross':
      // a/b = c/d ⟺ ad = bc, sans « recette » : on écrit l'égalité des rapports
      return [
        {
          tex: `\\frac{?}{${nTex(m.x2)}} = \\frac{${nTex(m.y1)}}{${nTex(m.x1)}}`,
          say: 'Le rapport bas ÷ haut est le même dans les deux colonnes.',
        },
        {
          tex: `? \\times ${nTex(m.x1)} = ${nTex(m.y1)} \\times ${nTex(m.x2)} = ${texOf(mul(Y1, X2))}`,
          say: 'Deux fractions sont égales exactement quand leurs « produits en croix » sont égaux (a/b = c/d ⟺ ad = bc).',
        },
        {
          tex: `? = ${texOf(mul(Y1, X2))} \\div ${nTex(m.x1)} = ${res}`,
          say: `On cherche le nombre qui, multiplié par ${n(m.x1)}, donne ${show(mul(Y1, X2))} : on divise.`,
        },
      ];
  }
}

/** problèmes « jolis » : coefficient décimal court, réponse décimale exacte */
export function randomMissing(rng: () => number): Missing {
  for (;;) {
    const x1 = 2 + Math.floor(rng() * 7);
    const unit = [0.5, 1.2, 1.5, 2.4, 2.5, 3, 4, 6, 7.5, 12, 25][Math.floor(rng() * 11)]!;
    const x2 = 1 + Math.floor(rng() * 12);
    if (x2 === x1) continue;
    const y1 = Number((x1 * unit).toFixed(3));
    const r = missingValue({ x1, y1, x2 });
    if (r.q > 100) continue;
    return { x1, y1, x2 };
  }
}

/* ------------------------------------------------------------------ */
/* partage proportionnel                                               */
/* ------------------------------------------------------------------ */

/** « ≈ 58,33 » quand une fraction n'a pas d'écriture décimale finie, sinon '' */
export function approx(f: Frac): string {
  if (exact(f).plain.includes('/')) return `≈ ${formatNumber(f.p / f.q, 2)}`;
  return '';
}
const approxTex = (f: Frac) =>
  approx(f) ? ` \\approx ${approx(f).slice(2).replace(',', '{,}')}` : '';

export function shareSteps(
  total: number,
  parts: readonly number[],
): { shares: Frac[]; part: Frac; steps: Step[] } {
  const count = parts.reduce((s, p) => s + p, 0);
  const T = fx(total);
  const part = div(T, frac(count));
  const shares = parts.map((p) => mul(part, frac(p)));
  const names = parts.map((_, i) => String.fromCharCode(65 + i));
  return {
    part,
    shares,
    steps: [
      {
        tex: `${parts.join(' + ')} = ${count}`,
        say: `Le ratio ${parts.join(' : ')} partage le tout en ${count} parts égales.`,
      },
      {
        tex: `${nTex(total)} \\div ${count} = ${texOf(part)}${approxTex(part)}`,
        say: `Une part vaut ${show(part)}${approx(part) ? ` (${approx(part)})` : ''}.`,
      },
      ...parts.map((p, i) => ({
        tex: `${names[i]} : ${p} \\times ${texOf(part)} = ${texOf(shares[i]!)}${approxTex(shares[i]!)}`,
        say: `${names[i]} reçoit ${p} part${p > 1 ? 's' : ''}.`,
      })),
      {
        tex: `${shares.map(texOf).join(' + ')} = ${texOf(shares.reduce((s, x) => add(s, x), frac(0)))}`,
        say: 'Vérification : on retrouve le tout.',
      },
    ],
  };
}
